import { CloudUploadOutlined } from "@ant-design/icons";
import { Modal, Typography, Spin, List, Avatar, message, Alert } from "antd";
import Dragger from "antd/es/upload/Dragger";
import React, { useState } from "react";
import axios from "axios";
import { openNotification } from "../../../../Components/Notification";
import bulkUploadService from "../../../../Service/bulk.upload.service";
import { classifyError } from "../../../../Utils/errorClassifier";
import { ERROR_MESSAGES } from "../../../../Utils/errorMessages";

interface UploadModalProps {
  showUploadModal: boolean;
  setShowUploadModal: (show: boolean) => void;
  jobDescriptionId: string;
  onUploadSuccess?: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({
  setShowUploadModal,
  showUploadModal,
  jobDescriptionId,
  onUploadSuccess,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<any>(null);

  const processSteps = [
    {
      title: "Upload Resumes in Bulk",
      description:
        "Admins can upload a ZIP file containing multiple resumes, and the system will extract and save details for all candidates.",
    },
    {
      title: "AI Profile Matching for All Candidates",
      description:
        "The extracted details for each candidate will be matched against job requirements using AI to determine their suitability.",
    },
    {
      title: "AI-Powered Interviews",
      description:
        "Qualified candidates will automatically receive an email invitation to participate in AI-driven interviews.",
    },
  ];

  const getPresignedUrl = async (
    fileName: string,
    fileType: string,
    JobDescriptionId: string
  ) => {
    try {
      const data = await bulkUploadService.getUploadPresignedUrl(
        fileName,
        fileType,
        JobDescriptionId
      );
      return data;
    } catch (error) {
      console.error("🚀 ~ error:", error);
      throw error;
    }
  };

  const startProcessing = async (
    jobDescriptionId: string,
    fileName: string
  ) => {
    try {
      const response = await bulkUploadService.startFileProcessing(
        jobDescriptionId,
        fileName
      );
      return response;
    } catch (error) {
      console.error("🚀 ~ startProcessing ~ error:", error);
      throw error;
    }
  };

  const uploadFile = async (file: File) => {
    try {
      setUploadError(null);
      setIsUploading(true);

      if (
        file.type !== "application/zip" &&
        file.type !== "application/x-zip-compressed"
      ) {
        openNotification("error", "Only ZIP files are allowed", "");
        return;
      }

      const { url, fileName } = await getPresignedUrl(
        file.name,
        file.type,
        jobDescriptionId
      );

      console.debug("Uploading to URL:", url);

      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });
      message.success("File uploaded successfully. Processing started.");
      const processResponse = await startProcessing(jobDescriptionId, fileName);
      if (processResponse?.status === 201) {
        message.success("Job creation started successfully");
      } else {
        message.error("Failed to start job creation process");
      }
      if (onUploadSuccess) onUploadSuccess();
      handleClose();
    } catch (error: any) {
      console.error("Upload failed:", error);
      const classified = classifyError(error);
      const msgs = ERROR_MESSAGES[classified.category];
      setUploadError({
        headline: msgs.recruiterHeadline,
        body: msgs.recruiterBody,
        guidance: msgs.recruiterGuidance,
        isRetryable: classified.isRetryable,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const props = {
    name: "file",
    multiple: false,
    accept: ".zip",
    beforeUpload: (file: File) => {
      uploadFile(file);
      return false;
    },
    onDrop(e: React.DragEvent<HTMLDivElement>) {
      console.debug("Dropped files", e.dataTransfer.files);
    },
  };

  const handleClose = () => {
    setShowUploadModal(false);
    setUploadError(null);
  };

  return (
    <Modal
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          Upload File
        </Typography.Title>
      }
      open={showUploadModal}
      centered
      footer={null}
      onCancel={handleClose}
      confirmLoading={isUploading}
      destroyOnClose
      width={600}
    >
      {uploadError && (
        <Alert
          type="error"
          showIcon
          message={uploadError.headline}
          description={
            <div>
              <p style={{ margin: '8px 0 0 0' }}>{uploadError.body}</p>
              {uploadError.guidance && uploadError.guidance.length > 0 && (
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                  {uploadError.guidance.map((item: string, idx: number) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          }
          style={{ marginBottom: '16px' }}
        />
      )}

      <List
        itemLayout="horizontal"
        dataSource={processSteps}
        renderItem={(item, index) => (
          <List.Item>
            <List.Item.Meta
              avatar={<Avatar>{index + 1}</Avatar>}
              title={item.title}
              description={item.description}
            />
          </List.Item>
        )}
      />

      <Spin spinning={isUploading} tip="Uploading...">
        <Dragger {...props} disabled={isUploading}>
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined />
          </p>
          <p className="ant-upload-text">
            {isUploading
              ? "Uploading file..."
              : "Click or drag a ZIP file to this area to upload"}
          </p>
        </Dragger>
      </Spin>
    </Modal>
  );
};

export default UploadModal;
